import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/api';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';

export default function MyProfile() {
    const { user, token, updateUser } = useAuth();
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const [form, setForm] = useState({
        name: '', phone: '', title: '', address: '', city: '', state: '', zip: '', about: '',
        facebook: '', twitter: '', googleplus: '', linkedin: '',
        projectsSince: '', projectsCompleted: '0', projectsOngoing: '0', projectsUpcoming: '0'
    });

    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || '',
                phone: user.phone || '',
                title: user.title || '',
                address: user.address || '',
                city: user.city || '',
                state: user.state || '',
                zip: user.zip || '',
                about: user.about || '',
                facebook: user.facebook || '',
                twitter: user.twitter || '',
                googleplus: user.googleplus || '',
                linkedin: user.linkedin || '',
                projectsSince: user.projectsSince || '',
                projectsCompleted: user.projectsCompleted || '0',
                projectsOngoing: user.projectsOngoing || '0',
                projectsUpcoming: user.projectsUpcoming || '0'
            });
            if (user.avatar) {
                setAvatarPreview(getImageUrl(user.avatar));
            }
        }
    }, [user]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setAvatarFile(file);
            setAvatarPreview(URL.createObjectURL(file));
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        setSaving(true);
        setMsg(null);

        let finalAvatar = user?.avatar;

        try {
            // Upload avatar first if changed
            if (avatarFile) {
                const uploadRes = await api.uploadImages(token, [avatarFile]);
                if (uploadRes.urls && uploadRes.urls.length > 0) {
                    finalAvatar = uploadRes.urls[0];
                } else {
                    throw new Error('Avatar upload failed');
                }
            }

            const data = await api.updateMe(token, { ...form, avatar: finalAvatar });
            if (data._id) {
                updateUser(data);
                setMsg({ type: 'success', text: 'Profile and avatar updated successfully!' });
            } else {
                setMsg({ type: 'error', text: data.message || 'Failed to update profile' });
            }
        } catch (err: any) {
            setMsg({ type: 'error', text: err.message || 'Server error. Please try again.' });
        }
        setSaving(false);
    };

    return (
        <UserDashboardLayout>
            <div className="mb-8">
                <h2 className="text-3xl font-black text-black">My Account Settings</h2>
                <p className="text-black font-bold tracking-tight uppercase text-[10px]">Manage your identity and privacy across the Guru platform.</p>
            </div>

            {msg && (
                <div className={`p-4 rounded-2xl mb-8 flex items-center gap-3 font-bold border ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-check-circle' : 'fa-circle-exclamation'}`}></i>
                    {msg.text}
                </div>
            )}

            <form onSubmit={handleSave} className="space-y-8">
                <div className="bg-white rounded-[2rem] border border-[#05144b]/10 shadow-sm p-8 lg:p-10">
                    <div className="flex items-center gap-4 mb-10 pb-4 border-b border-[#05144b]/5">
                        <div className="w-10 h-10 bg-white border-2 border-black text-black rounded-xl flex items-center justify-center font-black">01</div>
                        <h4 className="text-xl font-bold text-black uppercase italic tracking-tighter">Identity & Branding</h4>
                    </div>
                    
                    <div className="flex flex-col md:flex-row gap-12 items-start">
                        {/* Avatar Upload Section */}
                        <div className="flex flex-col items-center gap-4 relative group">
                            <div className="w-40 h-40 rounded-[2.5rem] overflow-hidden border-4 border-slate-50 shadow-xl relative bg-blue-50">
                                {avatarPreview ? (
                                    <img src={avatarPreview} className="w-full h-full object-cover" alt="Profile Preview" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[#05144b]/20">
                                        <i className="fa-solid fa-user-tie text-5xl"></i>
                                    </div>
                                )}
                                <label className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                    <input type="file" className="hidden" accept="image/*" onChange={handleAvatarChange} />
                                    <div className="w-12 h-12 bg-white text-[#05144b] rounded-full flex items-center justify-center shadow-lg border border-[#05144b]/10">
                                        <i className="fa-solid fa-camera text-lg"></i>
                                    </div>
                                </label>
                            </div>
                            <div className="text-center">
                                <p className="text-[10px] font-black text-black uppercase tracking-widest mb-1">Profile Photo</p>
                                <span className="text-[9px] font-bold text-black">PNG / JPG (Max 5MB)</span>
                            </div>
                        </div>

                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                            <div className="form-group space-y-2">
                                <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Full Name</label>
                                <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="name" value={form.name} onChange={handleChange} />
                            </div>
                            <div className="form-group space-y-2">
                                <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Official Email</label>
                                <input type="email" className="w-full px-6 py-5 bg-slate-100 border border-slate-200 rounded-2xl text-black font-black cursor-not-allowed opacity-70" value={user?.email || ''} readOnly disabled />
                            </div>
                            <div className="form-group space-y-2">
                                <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Professional Title</label>
                                <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="title" value={form.title} onChange={handleChange} placeholder="e.g. Senior Broker" />
                            </div>
                            <div className="form-group space-y-2">
                                <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Contact Number</label>
                                <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="phone" value={form.phone} onChange={handleChange} />
                            </div>
                        </div>
                    </div>

                    {/* Corporate Heritage & Stats */}
                    <div className="mt-10 pt-10 border-t border-black/5">
                        <div className="flex items-center gap-4 mb-10">
                            <div className="w-10 h-10 bg-white border-2 border-black text-black rounded-xl flex items-center justify-center font-black">02</div>
                            <h4 className="text-xl font-bold text-black uppercase italic tracking-tighter">Corporate Heritage / About</h4>
                        </div>
                        
                        <div className="mb-8">
                            <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Provide a compelling description...</label>
                            <div className="position-relative">
                                <i className="fa-solid fa-quote-left text-slate-100 position-absolute top-0 start-0 m-4 fs-3"></i>
                                <textarea className="w-full px-6 py-8 ps-12 bg-white border-2 border-slate-200 rounded-[2rem] outline-none transition-all font-black text-black min-h-[160px]" 
                                    name="about" value={form.about} onChange={handleChange} placeholder="Describe the professional background..."></textarea>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="p-6 bg-white border-2 border-slate-100 rounded-[2rem] text-center shadow-sm">
                                <label className="text-[10px] font-black text-black uppercase tracking-widest mb-3 block">Since (year)</label>
                                <input type="text" className="w-full text-center bg-slate-50 border-2 border-slate-200 py-3 text-black font-black rounded-2xl outline-none focus:border-black transition-all" 
                                    name="projectsSince" value={form.projectsSince} onChange={handleChange} placeholder="e.g. 1995" />
                            </div>
                            <div className="p-6 bg-white border-2 border-slate-100 rounded-[2rem] text-center shadow-sm">
                                <label className="text-[10px] font-black text-black uppercase tracking-widest mb-3 block">Completed</label>
                                <input type="text" className="w-full text-center bg-slate-50 border-2 border-slate-200 py-3 text-emerald-600 font-black rounded-2xl outline-none focus:border-black transition-all" 
                                    name="projectsCompleted" value={form.projectsCompleted} onChange={handleChange} />
                            </div>
                            <div className="p-6 bg-white border-2 border-slate-100 rounded-[2rem] text-center shadow-sm">
                                <label className="text-[10px] font-black text-black uppercase tracking-widest mb-3 block">Ongoing</label>
                                <input type="text" className="w-full text-center bg-slate-50 border-2 border-slate-200 py-3 text-blue-600 font-black rounded-2xl outline-none focus:border-black transition-all" 
                                    name="projectsOngoing" value={form.projectsOngoing} onChange={handleChange} />
                            </div>
                            <div className="p-6 bg-white border-2 border-slate-100 rounded-[2rem] text-center shadow-sm">
                                <label className="text-[10px] font-black text-black uppercase tracking-widest mb-3 block">Upcoming</label>
                                <input type="text" className="w-full text-center bg-slate-50 border-2 border-slate-200 py-3 text-orange-500 font-black rounded-2xl outline-none focus:border-black transition-all" 
                                    name="projectsUpcoming" value={form.projectsUpcoming} onChange={handleChange} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-[2rem] border border-black/10 shadow-sm p-8 lg:p-10">
                    <div className="flex items-center gap-4 mb-8 pb-4 border-b border-black/5">
                        <div className="w-10 h-10 bg-white border-2 border-black text-black rounded-xl flex items-center justify-center font-black">03</div>
                        <h4 className="text-xl font-bold text-black uppercase italic tracking-tighter">Address & Background</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="form-group space-y-2 md:col-span-2">
                            <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Residential / Office Address</label>
                            <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="address" value={form.address} onChange={handleChange} />
                        </div>
                        <div className="form-group space-y-2">
                            <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Current City</label>
                            <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="city" value={form.city} onChange={handleChange} />
                        </div>
                        <div className="form-group space-y-2">
                            <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Zip Code</label>
                            <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="zip" value={form.zip} onChange={handleChange} />
                        </div>
                        </div>
                </div>

                <div className="bg-white rounded-[2rem] border border-black/10 shadow-sm p-8 lg:p-10">
                    <div className="flex items-center gap-4 mb-8 pb-4 border-b border-black/5">
                        <div className="w-10 h-10 bg-white border-2 border-black text-black rounded-xl flex items-center justify-center font-black">04</div>
                        <h4 className="text-xl font-bold text-black uppercase italic tracking-tighter">Social Connections</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="form-group space-y-2">
                            <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Facebook URL</label>
                            <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="facebook" value={form.facebook} onChange={handleChange} placeholder="https://facebook.com/..." />
                        </div>
                        <div className="form-group space-y-2">
                            <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Twitter URL</label>
                            <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="twitter" value={form.twitter} onChange={handleChange} placeholder="https://twitter.com/..." />
                        </div>
                        <div className="form-group space-y-2">
                            <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">LinkedIn URL</label>
                            <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="linkedin" value={form.linkedin} onChange={handleChange} placeholder="https://linkedin.com/in/..." />
                        </div>
                        <div className="form-group space-y-2">
                            <label className="text-[10px] font-black text-black uppercase tracking-widest ml-1 italic font-serif">Google Plus URL</label>
                            <input type="text" className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-2xl outline-none transition-all font-black text-black" name="googleplus" value={form.googleplus} onChange={handleChange} placeholder="https://plus.google.com/..." />
                        </div>
                    </div>
                </div>

                {/* Final step */}
                <div className="bg-white rounded-[2.5rem] p-10 text-center shadow-md border-2 border-black/10">
                    <h3 className="text-2xl font-black text-black mb-2 uppercase italic tracking-tighter">Save Global Changes</h3>
                    <p className="text-black mb-10 max-w-lg mx-auto uppercase tracking-widest text-[9px] font-black">Updated information will be reflected instantly across your dashboard and public profile.</p>
                    <button className="bg-white text-black border-2 border-black px-16 py-5 rounded-3xl font-black text-[13px] uppercase tracking-[4px] transition-all shadow-xl shadow-slate-100 transform hover:-translate-y-1 active:scale-95 disabled:opacity-50" type="submit" disabled={saving}>
                        {saving ? <><i className="fa-solid fa-circle-notch fa-spin mr-3"></i> Syncing Profile...</> : 'Launch Profile Sync'}
                    </button>
                </div>
            </form>
        </UserDashboardLayout>
    );
}



